import { PayoutType } from "@/types/payouts"
import { formatCurrency, formatDatetime } from "@/utils/format"
import { Box, HStack, Icon, IconButton, Text, VStack } from "@chakra-ui/react"
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
} from "lucide-react"

type Props = {
  payout: PayoutType
}

export const PayoutReceipt = ({ payout }: Props) => {
  if (!payout) return null

  const getStatusIcon = () => {
    switch (payout.status) {
      case "completed":
        return <CheckCircle2 size={48} color="var(--chakra-colors-green-500)" />
      case "declined":
        return <AlertCircle size={48} color="var(--chakra-colors-red-500)" />
      case "cancelled":
        return <AlertCircle size={48} color="var(--chakra-colors-red-500)" />
      default:
        return <Clock size={48} color="var(--chakra-colors-orange-500)" />
    }
  }

  const getStatusText = () => {
    switch (payout.status) {
      case "completed":
        return "¡Pago Exitoso!"
      case "declined":
        return "Pago Rechazado"
      case "cancelled":
        return "Pago Cancelado"
      case "processing":
        return "Pago en Proceso"
      default:
        return "Pago Pendiente"
    }
  }

  return (
    <VStack gap="0" align="stretch" width="full">
      <Box bg="brand.muted" px="6" py="8" width="full" position="relative">
        <VStack gap="4" align="center">
          <Box p="3" bg="bg.panel" borderRadius="2xl" boxShadow="sm">
            {getStatusIcon()}
          </Box>
          <VStack gap="1" align="center">
            <Text fontWeight="bold" fontSize="lg" color="fg.default">
              {getStatusText()}
            </Text>
            <Text fontSize="2xl" fontWeight="800" color="brand.fg">
              {formatCurrency(payout.amount)}
            </Text>
          </VStack>
        </VStack>
      </Box>

      <Box px="6" py="6" bg="bg.panel" borderRadius="b-2xl">
        <VStack gap="0" align="stretch">
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="fg.subtle"
            mb="2"
            letterSpacing="widest"
          >
            DETALLES DE LA TRANSACCIÓN
          </Text>

          <DetailRow label="Referencia" value={payout.id} isCopyable />
          <DetailRow label="Fecha" value={formatDatetime(payout.createdAt)} />
          {payout.payoutItems?.length === 1 && (
            <DetailRow
              label={
                payout.type === "buildingManagement"
                  ? "Inmueble"
                  : "Descripción"
              }
              value={payout.payoutItems[0].description}
            />
          )}
          {(payout.payoutItems?.length ?? 0) > 1 &&
            payout.payoutItems?.map((item) => (
              <DetailRow
                key={item.description}
                label={item.description}
                value={formatCurrency(item.amount)}
              />
            ))}
          {payout.bank ? (
            <>
              <DetailRow label="Banco" value={payout.bank} />
              <DetailRow
                label="Cuenta"
                value={`${payout.accountType} ${payout.accountNumber}`}
              />
            </>
          ) : (
            <DetailRow
              label="Referencia del pago"
              value={payout.manualReference ?? "-"}
            />
          )}
          <DetailRow label="Inmobiliaria" value={payout.merchantName} />
        </VStack>

        <Box
          mt="8"
          p="4"
          bg="bg.muted"
          borderRadius="xl"
          border="1px dashed"
          borderColor="border.subtle"
        >
          <HStack gap="3" align="center">
            <Icon color="brand.fg">
              <ExternalLink size={16} />
            </Icon>
            <Text fontSize="xs" color="fg.muted">
              Este pago se verá reflejado en tu extracto como{" "}
              <strong>PALOMMA SAS</strong>.
            </Text>
          </HStack>
        </Box>
      </Box>
    </VStack>
  )
}

const DetailRow = ({
  label,
  value,
  isBold = false,
  isCopyable = false,
}: {
  label: string
  value: string
  isBold?: boolean
  isCopyable?: boolean
}) => (
  <HStack
    justify="space-between"
    py="3"
    borderBottomWidth="1px"
    borderColor="border.subtle"
  >
    <Text color="fg.muted" fontSize="sm">
      {label}
    </Text>
    <HStack gap="2">
      <Text
        fontWeight={isBold ? "bold" : "medium"}
        fontSize="sm"
        textAlign="right"
        color="fg.default"
      >
        {value}
      </Text>
      {isCopyable && (
        <IconButton
          size="xs"
          variant="ghost"
          color="fg.subtle"
          aria-label="Copy"
          onClick={() =>
            navigator.clipboard
              .writeText(value)
              .catch((e) => console.error("Failed to copy text: ", e))
          }
        >
          <Copy size={12} />
        </IconButton>
      )}
    </HStack>
  </HStack>
)
